import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Eye, EyeOff, ArrowRight, ArrowLeft } from 'lucide-react';
import api from '../../services/api';
import { L, kicker as luxeKicker, display, btn, LuxeStyles, useIsDesktop, DiamondMark } from '../../theme/luxe';

// Small/secondary text is never lighter than #6E685F (Luxe staff rule).
const MUTE = '#6E685F';
const kicker = (extra = {}) => luxeKicker({ color: MUTE, ...extra });

const Login = () => {
  const navigate = useNavigate();
  const { login, setUser } = useAuth();
  const { t, language, setLanguage: switchLanguage } = useLanguage();
  // Auth pages default to Chinese on first mount in the session; user can still switch manually after.
  useEffect(() => {
    if (!sessionStorage.getItem('auth_lang_touched')) {
      sessionStorage.setItem('auth_lang_touched', '1');
      if (language !== 'zh') switchLanguage('zh');
    }
  }, []); // eslint-disable-line
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const [countryCode, setCountryCode] = useState('+1'); // Default to US
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: '',
    otp: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(null); // null, 'email', 'reset'
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Common country codes for phone OTP
  const countryCodes = [
    { code: '+1', country: 'US/CA', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+86', country: 'CN', flag: '🇨🇳' },
    { code: '+852', country: 'HK', flag: '🇭🇰' },
    { code: '+886', country: 'TW', flag: '🇹🇼' },
    { code: '+65', country: 'SG', flag: '🇸🇬' },
    { code: '+81', country: 'JP', flag: '🇯🇵' },
    { code: '+82', country: 'KR', flag: '🇰🇷' },
    { code: '+61', country: 'AU', flag: '🇦🇺' },
    { code: '+49', country: 'DE', flag: '🇩🇪' },
    { code: '+33', country: 'FR', flag: '🇫🇷' },
    { code: '+39', country: 'IT', flag: '🇮🇹' },
    { code: '+34', country: 'ES', flag: '🇪🇸' },
    { code: '+31', country: 'NL', flag: '🇳🇱' },
    { code: '+41', country: 'CH', flag: '🇨🇭' },
    { code: '+46', country: 'SE', flag: '🇸🇪' },
    { code: '+47', country: 'NO', flag: '🇳🇴' },
    { code: '+45', country: 'DK', flag: '🇩🇰' },
    { code: '+358', country: 'FI', flag: '🇫🇮' },
    { code: '+48', country: 'PL', flag: '🇵🇱' },
    { code: '+43', country: 'AT', flag: '🇦🇹' },
    { code: '+32', country: 'BE', flag: '🇧🇪' },
    { code: '+351', country: 'PT', flag: '🇵🇹' },
    { code: '+353', country: 'IE', flag: '🇮🇪' },
    { code: '+64', country: 'NZ', flag: '🇳🇿' },
    { code: '+91', country: 'IN', flag: '🇮🇳' },
    { code: '+60', country: 'MY', flag: '🇲🇾' },
    { code: '+66', country: 'TH', flag: '🇹🇭' },
    { code: '+63', country: 'PH', flag: '🇵🇭' },
    { code: '+62', country: 'ID', flag: '🇮🇩' },
    { code: '+84', country: 'VN', flag: '🇻🇳' },
    { code: '+55', country: 'BR', flag: '🇧🇷' },
    { code: '+52', country: 'MX', flag: '🇲🇽' },
    { code: '+54', country: 'AR', flag: '🇦🇷' },
    { code: '+57', country: 'CO', flag: '🇨🇴' },
    { code: '+56', country: 'CL', flag: '🇨🇱' },
    { code: '+27', country: 'ZA', flag: '🇿🇦' },
    { code: '+971', country: 'AE', flag: '🇦🇪' },
    { code: '+966', country: 'SA', flag: '🇸🇦' },
    { code: '+972', country: 'IL', flag: '🇮🇱' },
    { code: '+90', country: 'TR', flag: '🇹🇷' },
    { code: '+7', country: 'RU', flag: '🇷🇺' },
  ];

  // Format phone number for Twilio E.164 format (e.g., +16262973443)
  const formatPhoneForTwilio = (phone) => {
    // Remove all non-digit characters except leading +
    const digits = phone.replace(/[^\d]/g, '');
    return `${countryCode}${digits}`;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  // Roles allowed into the admin dashboard. Anyone else (e.g. plain
  // `profile` users / unprivileged signups) is bounced to /client even if
  // their credentials check out, so they're never stranded inside an admin
  // shell that has nothing for them.
  const ADMIN_ALLOWED_ROLES = new Set([
    'super_admin',
    'admin',
    'supervisor_coach',
    'supervisor',
    'leader_coach',
    'coach',
    'matchmaker', // covers both internal and external matchmaker_type
    'interviewer',
  ]);

  // Returns true and clears auth + redirects when the user's role is not
  // permitted. Returns false to let the caller continue the normal flow.
  const denyIfNotAdminRole = (userData) => {
    const role = String(userData?.role || '').toLowerCase();
    // Also accept secondary roles from the multi-role array (set by backend
    // /auth/me + login). Without this, a user whose primary role is empty
    // but who has e.g. `interviewer` in user_roles was being kicked to the
    // client portal after SMS login.
    const rolesArr = Array.isArray(userData?.roles)
      ? userData.roles.map(r => String(r || '').toLowerCase())
      : [];
    const allRoles = [role, ...rolesArr].filter(Boolean);
    if (allRoles.some(r => ADMIN_ALLOWED_ROLES.has(r))) return false;
    // Empty role — treat as "not yet loaded" and let the user proceed
    // rather than yanking them to /client. The dashboard route guards
    // will block them if they're truly unauthorised.
    if (!role && rolesArr.length === 0) return false;
    // Strip the admin token + state so we don't leave them half-logged-in
    // inside the admin app.
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setError(language === 'zh'
      ? '此账号无管理后台权限，正在跳转至客户端登录…'
      : 'This account does not have admin access. Redirecting to the client portal…');
    setTimeout(() => { window.location.assign(`${process.env.PUBLIC_URL}/client`); }, 1600);
    return true;
  };

  // Translate backend login errors
  const translateLoginError = (errorMsg) => {
    if (!errorMsg) return t('auth.loginFailed');
    const msg = errorMsg.toLowerCase();
    if (msg.includes('email') && msg.includes('password') && msg.includes('required')) return t('auth.emailPasswordRequired');
    if (msg.includes('invalid credentials') || msg.includes('status code 401')) return t('auth.invalidCredentials');
    if (msg.includes('inactive') || msg.includes('suspended')) return t('auth.accountSuspended');
    if (msg.includes('invalid response') || msg.includes('status code 500') || msg.includes('server error')) return t('auth.serverError');
    if (msg.includes('network') || msg.includes('econnrefused') || msg.includes('err_network') || msg.includes('timeout')) return t('auth.networkError');
    if (msg.includes('request failed') || msg.includes('login failed')) return t('auth.loginFailed');
    return errorMsg;
  };

  // Email/Password login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!formData.email || !formData.password) {
      setError(t('auth.emailPasswordRequired'));
      setLoading(false);
      return;
    }

    const result = await login(formData.email, formData.password);

    if (result.success) {
      if (denyIfNotAdminRole(result.user)) {
        setLoading(false);
        return;
      }
      navigate('/', { replace: true });
    } else {
      setError(translateLoginError(result.error));
    }

    setLoading(false);
  };

  // Send OTP to phone
  const handleSendOTP = async () => {
    // Remove non-digits from phone for validation
    const phoneDigits = formData.phone.replace(/[^\d]/g, '');
    if (!phoneDigits || phoneDigits.length < 6) {
      setError(t('auth.enterValidPhone'));
      return;
    }

    setOtpLoading(true);
    setError('');

    try {
      // Send phone in E.164 format: +countrycode + digits only
      const formattedPhone = formatPhoneForTwilio(formData.phone);
      await api.post('/auth/send-otp', { phone: formattedPhone });
      setOtpSent(true);
      setSuccess(t('auth.otpSentSuccess'));
      
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || t('auth.otpSendFailed'));
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP and login
  const handleOTPLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.otp || formData.otp.length < 4) {
      setError(t('auth.enterValidOtp'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Send phone in E.164 format for verification
      const formattedPhone = formatPhoneForTwilio(formData.phone);
      const response = await api.post('/auth/verify-otp', { 
        phone: formattedPhone, 
        otp: formData.otp 
      });
      
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        setUser(response.data.user);
        if (denyIfNotAdminRole(response.data.user)) {
          setLoading(false);
          return;
        }
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || t('auth.otpInvalid'));
    } finally {
      setLoading(false);
    }
  };

  // Forgot password - send reset request
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      setError(t('auth.enterEmail'));
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/forgot-password', { email: forgotEmail });
      setSuccess(response.data.emailSent ? t('auth.resetCodeSentEmail') : t('auth.resetLinkSent'));
      setForgotMode('reset');
    } catch (err) {
      setError(err.response?.data?.error || t('auth.resetRequestFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Reset password with token
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetToken) {
      setError(t('auth.enterResetToken'));
      return;
    }
    if (newPassword.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsNoMatch'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/auth/reset-password', { 
        token: resetToken, 
        newPassword 
      });
      setSuccess(t('auth.passwordResetSuccess'));
      setTimeout(() => {
        exitForgotMode();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || t('auth.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  const exitForgotMode = () => {
    setForgotMode(null);
    setError('');
    setSuccess('');
    setForgotEmail('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
  };


  // ─── Presentation (Luxe) ───────────────────────────────────────────────────
  const isDesktop = useIsDesktop(900);
  const isZh = language === 'zh';
  const tr = (zh, en) => (isZh ? zh : en);

  const PAGE_CSS = `
.sl-tab { position: relative; }
.sl-tab::after { content:''; position:absolute; left:0; right:0; bottom:-1px; height:1px; background:${L.ink};
  transform:scaleX(0); transform-origin:left; transition:transform .5s ${L.easeOut}; }
.sl-tab.sl-on::after { transform:scaleX(1); }
.sl-root .lx-input { border-bottom-color: rgba(17,17,17,.35); }
.sl-root .lx-input:focus { border-bottom-color: ${L.ink}; }
.sl-root .lx-input::placeholder { color: ${MUTE}; }
.sl-root a { color: ${L.goldDeep}; }
.sl-select { appearance:none; -webkit-appearance:none; cursor:pointer; }
.sl-select:disabled { cursor:not-allowed; color:${MUTE}; }
.lx-input:disabled { color:${MUTE}; }
.sl-eye:hover { color: ${L.ink} !important; }
.sl-ghost:hover:not(:disabled) { background: ${L.ink} !important; color: ${L.paper} !important; }
.sl-btn-disabled.lx-btn:hover { background: ${L.ink} !important; color: ${L.paper} !important; }
@keyframes slMsg { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:none; } }
.sl-msg { animation: slMsg .45s ${L.easeOut} backwards; }
@media (prefers-reduced-motion: reduce) { .sl-msg { animation:none; } }
`;

  const labelStyle = kicker({ display: 'block', fontSize: 12, letterSpacing: '.22em', marginBottom: 2 });
  const field = { marginBottom: 22, position: 'relative' };
  const spinner = (dark) => ({
    width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
    border: `1.5px solid ${dark ? 'rgba(17,17,17,.18)' : 'rgba(246,243,238,.3)'}`,
    borderTopColor: dark ? L.ink : L.paper,
    animation: 'lxSpin .7s linear infinite',
  });
  const textLink = {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontFamily: L.sans, fontSize: 12.5, fontWeight: 500, letterSpacing: '.16em',
    textTransform: 'uppercase', color: L.ink,
  };
  const primaryBtn = (busy) => ({
    ...btn.primary, width: '100%', height: 46, padding: '0 26px', fontSize: 12.5, letterSpacing: '.3em',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.8 : 1,
  });
  const bodyText = {
    fontFamily: L.sans, fontWeight: 300, fontSize: 14, lineHeight: 1.6, color: L.ink2, margin: '0 0 24px',
  };
  const codeInput = { textAlign: 'center', letterSpacing: '.4em', fontFamily: L.serif, fontSize: 22, fontWeight: 500 };

  const Wordmark = ({ size }) => (
    <div style={{
      fontFamily: L.serif, fontWeight: 500, fontSize: size, lineHeight: 1,
      letterSpacing: '.28em', marginRight: '-.28em', color: L.ink,
    }}>FINDU</div>
  );

  const messages = (
    <>
      {error && (
        <div className="sl-msg" role="alert" style={{
          borderLeft: `2px solid ${L.danger}`, background: 'rgba(158,43,37,.05)', color: L.danger,
          padding: '11px 14px', fontSize: 13.5, lineHeight: 1.5, marginBottom: 22, fontWeight: 400,
        }}>{error}</div>
      )}
      {success && (
        <div className="sl-msg" role="status" style={{
          borderLeft: `2px solid ${L.gold}`, background: 'rgba(184,151,90,.08)', color: L.goldDeep,
          padding: '11px 14px', fontSize: 13.5, lineHeight: 1.5, marginBottom: 22, fontWeight: 400,
        }}>{success}</div>
      )}
    </>
  );

  const eyeBtn = (shown, toggle) => (
    <button type="button" className="sl-eye"
      aria-label={shown ? tr('隐藏密码', 'Hide password') : tr('显示密码', 'Show password')}
      style={{
        position: 'absolute', right: 0, bottom: 10, background: 'none', border: 'none',
        cursor: 'pointer', color: MUTE, display: 'flex', alignItems: 'center', padding: 4,
        transition: `color .3s ${L.ease}`,
      }}
      onClick={toggle}>
      {shown ? <EyeOff size={17} strokeWidth={1.4} /> : <Eye size={17} strokeWidth={1.4} />}
    </button>
  );

  // Left half on desktop: an editorial "cover" composed in type.
  const coverPanel = (
    <aside style={{
      position: 'relative', flex: '1 1 50%', minHeight: '100vh', overflow: 'hidden',
      background: L.paper2, borderRight: `1px solid ${L.line}`,
    }}>
      <div aria-hidden="true" style={{ position: 'absolute', inset: 28, border: `1px solid ${L.line}`, pointerEvents: 'none' }} />

      <div style={{
        position: 'relative', zIndex: 2, height: '100%', minHeight: '100vh', boxSizing: 'border-box',
        padding: '64px 72px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      }}>
        <div className="lx-fade lx-d1" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={kicker({ color: L.ink2, fontSize: 11.5, letterSpacing: '.26em' })}>{tr('员工专区', 'Staff Quarters')}</span>
          <span style={kicker({ fontSize: 11.5, letterSpacing: '.26em' })}>{tr('仅限内部', 'Internal only')}</span>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div className="lx-fade-up lx-d2" style={{ display: 'flex', justifyContent: 'center', marginBottom: 26 }}>
            <DiamondMark size={46} />
          </div>
          <div className="lx-fade-up lx-d2" style={kicker({ color: L.goldDeep, fontSize: 12, letterSpacing: '.32em', marginBottom: 26 })}>
            The Atelier · 红娘工作室
          </div>
          <div className="lx-fade-up lx-d3"><Wordmark size="clamp(64px, 8.5vw, 132px)" /></div>
          <div className="lx-fade lx-d4" style={{ display: 'flex', justifyContent: 'center', margin: '34px 0 30px' }}>
            <div className="lx-rule lx-rule-draw" style={{ width: 120, background: L.goldSoft }} />
          </div>
          <p className="lx-fade-up lx-d5" style={display(30, {
            fontStyle: 'italic', fontWeight: 400, color: L.ink2, lineHeight: 1.3,
            maxWidth: 440, margin: '0 auto',
          })}>
            {tr('每一段良缘，都始于用心的成全。', 'Behind every match, a careful hand.')}
          </p>
        </div>

        <div className="lx-fade lx-d6" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <span style={kicker({ fontSize: 12, letterSpacing: '.26em' })}>{tr('员工入口', 'The Staff Entrance')}</span>
          <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 18, color: MUTE }}>Est. FINDU</span>
        </div>
      </div>
    </aside>
  );

  const mobileMasthead = (
    <header style={{ textAlign: 'center', padding: '68px 16px 8px' }}>
      <div className="lx-fade-up lx-d1" style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        <DiamondMark size={34} />
      </div>
      <div className="lx-fade-up lx-d1" style={kicker({ color: L.goldDeep, letterSpacing: '.28em', marginBottom: 16 })}>
        The Atelier · 红娘工作室
      </div>
      <div className="lx-fade-up lx-d2"><Wordmark size={52} /></div>
      <div className="lx-fade lx-d3" style={{ display: 'flex', justifyContent: 'center', marginTop: 22 }}>
        <div className="lx-rule lx-rule-draw" style={{ width: 64, background: L.goldSoft }} />
      </div>
    </header>
  );

  const langToggle = (
    <div style={{
      position: 'fixed', top: 18, right: isDesktop ? 28 : 16, zIndex: 200,
      display: 'inline-flex', alignItems: 'center', gap: 14,
      fontFamily: L.sans, fontSize: 12.5, fontWeight: 500, letterSpacing: '.18em',
    }}>
      {[
        { code: 'zh', label: '中文' },
        { code: 'en', label: 'EN' },
      ].map((opt, i) => {
        const active = language === opt.code;
        return (
          <React.Fragment key={opt.code}>
            {i > 0 && <span aria-hidden="true" style={{ width: 1, height: 12, background: L.line }} />}
            <button
              type="button"
              onClick={() => switchLanguage(opt.code)}
              className={`lx-link${active ? ' lx-active' : ''}`}
              aria-pressed={active}
              style={{
                background: 'none', borderTop: 0, borderLeft: 0, borderRight: 0, padding: '6px 0', cursor: 'pointer',
                borderBottom: `1px solid ${active ? L.ink : 'transparent'}`,
                color: active ? L.ink : MUTE, fontFamily: 'inherit', fontSize: 'inherit',
                fontWeight: 'inherit', letterSpacing: 'inherit',
              }}
            >
              {opt.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );

  // ====== FORGOT PASSWORD FLOW ======
  const forgotFlow = (
    <div className="lx-fade-up lx-d3">
      <button
        type="button"
        className="lx-link"
        onClick={exitForgotMode}
        style={{ ...textLink, display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 26, color: L.ink2 }}
      >
        <ArrowLeft size={15} strokeWidth={1.5} />
        {t('auth.backToSignIn')}
      </button>

      {messages}

      {/* Step 1: Enter Email */}
      {forgotMode === 'email' && (
        <form onSubmit={handleForgotPassword}>
          <p style={bodyText}>{t('auth.forgotPasswordDesc')}</p>
          <div style={field}>
            <label htmlFor="sl-forgot-email" style={labelStyle}>{t('auth.emailAddress')}</label>
            <input
              id="sl-forgot-email"
              type="email"
              className="lx-input"
              placeholder={t('auth.emailPlaceholder')}
              value={forgotEmail}
              onChange={(e) => { setForgotEmail(e.target.value); setError(''); }}
              required
            />
          </div>
          <button type="submit" disabled={loading}
            className={`lx-btn${loading ? ' sl-btn-disabled' : ''}`} style={{ ...primaryBtn(loading), marginTop: 8 }}>
            {loading ? <><span style={spinner(false)} /><span>{t('auth.sending')}</span></> : t('auth.sendResetLink')}
          </button>
        </form>
      )}

      {/* Step 2: Enter Code + New Password */}
      {forgotMode === 'reset' && (
        <form onSubmit={handleResetPassword}>
          <p style={bodyText}>{t('auth.enterCodeDesc')}</p>
          <div style={field}>
            <label htmlFor="sl-reset-code" style={labelStyle}>{t('auth.resetCodeLabel')}</label>
            <input
              id="sl-reset-code"
              type="text"
              className="lx-input"
              placeholder={t('auth.enterResetCode')}
              value={resetToken}
              onChange={(e) => { setResetToken(e.target.value); setError(''); }}
              required
              maxLength={6}
              style={resetToken ? codeInput : { textAlign: 'center' }}
            />
          </div>
          <div style={field}>
            <label htmlFor="sl-new-pass" style={labelStyle}>{t('auth.newPassword')}</label>
            <input
              id="sl-new-pass"
              type={showNewPassword ? 'text' : 'password'}
              className="lx-input"
              placeholder={t('auth.newPasswordPlaceholder')}
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
              required
              minLength={6}
              style={{ paddingRight: 36 }}
            />
            {eyeBtn(showNewPassword, () => setShowNewPassword(!showNewPassword))}
          </div>
          <div style={field}>
            <label htmlFor="sl-confirm-pass" style={labelStyle}>{t('auth.confirmPassword')}</label>
            <input
              id="sl-confirm-pass"
              type={showNewPassword ? 'text' : 'password'}
              className="lx-input"
              placeholder={t('auth.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              required
              minLength={6}
            />
          </div>
          <button type="submit" disabled={loading}
            className={`lx-btn${loading ? ' sl-btn-disabled' : ''}`} style={{ ...primaryBtn(loading), marginTop: 8 }}>
            {loading ? <><span style={spinner(false)} /><span>{t('auth.resetting')}</span></> : t('auth.resetPassword')}
          </button>
        </form>
      )}
    </div>
  );

  // ====== NORMAL LOGIN FLOW ======
  const loginFlow = (
    <>
      {/* Login Method Toggle */}
      <div className="lx-fade-up lx-d3" role="tablist" style={{
        display: 'flex', gap: 28, borderBottom: `1px solid ${L.line}`, marginBottom: 30,
      }}>
        {[
          { mode: 'email', label: t('auth.email'), onSelect: () => { setLoginMethod('email'); setError(''); setSuccess(''); } },
          { mode: 'phone', label: t('auth.phoneOtp'), onSelect: () => { setLoginMethod('phone'); setError(''); setSuccess(''); setOtpSent(false); } },
        ].map(tab => {
          const on = loginMethod === tab.mode;
          return (
            <button key={tab.mode} type="button" role="tab" aria-selected={on}
              className={`sl-tab${on ? ' sl-on' : ''}`}
              onClick={tab.onSelect}
              style={{
                background: 'none', border: 'none', padding: '12px 0', cursor: 'pointer',
                fontFamily: L.sans, fontSize: 13, fontWeight: 400, letterSpacing: '.18em',
                color: on ? L.ink : MUTE, transition: `color .3s ${L.ease}`,
              }}>
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="lx-fade-up lx-d4">
        {messages}

        {/* Email/Password Login Form */}
        {loginMethod === 'email' && (
          <form onSubmit={handleEmailLogin}>
            <div style={field}>
              <label htmlFor="sl-email" style={labelStyle}>{t('auth.emailAddress')}</label>
              <input
                id="sl-email"
                type="email"
                name="email"
                className="lx-input"
                placeholder={t('auth.emailPlaceholder')}
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div style={field}>
              <label htmlFor="sl-pass" style={labelStyle}>{t('auth.password')}</label>
              <input
                id="sl-pass"
                type={showPassword ? 'text' : 'password'}
                name="password"
                className="lx-input"
                placeholder={t('auth.passwordPlaceholder')}
                value={formData.password}
                onChange={handleChange}
                required
                style={{ paddingRight: 36 }}
              />
              {eyeBtn(showPassword, () => setShowPassword(!showPassword))}
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap', margin: '4px 0 30px',
            }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none',
                fontSize: 13.5, fontWeight: 300, color: L.ink2, lineHeight: 1.4,
              }}>
                <input type="checkbox" style={{ width: 15, height: 15, margin: 0, accentColor: L.ink, cursor: 'pointer' }} />
                <span>{t('auth.rememberMe')}</span>
              </label>
              <button
                type="button"
                className="lx-link"
                onClick={() => { setForgotMode('email'); setError(''); setSuccess(''); }}
                style={{ ...textLink, fontSize: 13, fontWeight: 400, letterSpacing: '.12em', textTransform: 'none', color: L.goldDeep }}
              >
                {t('auth.forgotPassword')}?
              </button>
            </div>

            <button type="submit" disabled={loading}
              className={`lx-btn${loading ? ' sl-btn-disabled' : ''}`} style={primaryBtn(loading)}>
              {loading ? <><span style={spinner(false)} /><span>{t('auth.signingIn')}</span></> : t('auth.signIn')}
            </button>
          </form>
        )}

        {/* Phone OTP Login Form */}
        {loginMethod === 'phone' && (
          <form onSubmit={handleOTPLogin}>
            <div style={field}>
              <label htmlFor="sl-phone" style={labelStyle}>{t('auth.phoneNumber')}</label>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                {/* Country Code Selector */}
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  disabled={otpSent}
                  className="lx-input sl-select"
                  aria-label={tr('国家/地区代码', 'Country code')}
                  style={{ width: 96, flexShrink: 0 }}
                >
                  {countryCodes.map(cc => (
                    <option key={cc.code} value={cc.code}>
                      {cc.flag} {cc.code}
                    </option>
                  ))}
                </select>

                {/* Phone Number Input */}
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    id="sl-phone"
                    type="tel"
                    name="phone"
                    className="lx-input"
                    placeholder="6262973443"
                    value={formData.phone}
                    onChange={(e) => {
                      // Only allow digits, spaces, hyphens, parentheses
                      const value = e.target.value.replace(/[^\d\s\-()]/g, '');
                      setFormData({ ...formData, phone: value });
                      setError('');
                    }}
                    disabled={otpSent}
                    style={{ paddingRight: otpSent ? 80 : 0 }}
                  />
                  {otpSent && (
                    <button
                      type="button"
                      className="lx-link"
                      onClick={() => { setOtpSent(false); setFormData({ ...formData, otp: '' }); }}
                      style={{ ...textLink, position: 'absolute', right: 0, bottom: 13, fontSize: 12.5, fontWeight: 400, textTransform: 'none', color: L.goldDeep }}
                    >
                      {t('auth.change')}
                    </button>
                  )}
                </div>
              </div>
              <p style={{ fontFamily: L.sans, fontSize: 12, fontWeight: 300, color: MUTE, margin: '8px 0 0' }}>
                {t('auth.phoneFormatHint')}: {countryCode}{formData.phone ? formData.phone.replace(/[^\d]/g, '') : '1234567890'}
              </p>
            </div>

            {!otpSent ? (
              <button
                type="button"
                onClick={handleSendOTP}
                disabled={otpLoading || !formData.phone}
                className={`lx-btn${(otpLoading || !formData.phone) ? ' sl-btn-disabled' : ''}`}
                style={{ ...primaryBtn(otpLoading || !formData.phone), marginTop: 8 }}
              >
                {otpLoading ? (
                  <>
                    <span style={spinner(false)} />
                    <span>{t('auth.sendingOtp')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('auth.sendOtp')}</span>
                    <ArrowRight size={16} strokeWidth={1.5} />
                  </>
                )}
              </button>
            ) : (
              <>
                <div style={field}>
                  <label htmlFor="sl-otp" style={labelStyle}>{t('auth.enterOtp')}</label>
                  <input
                    id="sl-otp"
                    type="text"
                    name="otp"
                    className="lx-input"
                    placeholder={t('auth.otpPlaceholder')}
                    value={formData.otp}
                    onChange={handleChange}
                    maxLength={6}
                    style={formData.otp ? codeInput : { textAlign: 'center' }}
                  />
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: 12, flexWrap: 'wrap', marginTop: 10, fontSize: 13, fontWeight: 300,
                  }}>
                    <span style={{ color: L.ink2 }}>{t('auth.didntReceiveOtp')}</span>
                    {countdown > 0 ? (
                      <span style={{ color: MUTE, fontVariantNumeric: 'tabular-nums' }}>{t('auth.resendIn')} {countdown}s</span>
                    ) : (
                      <button
                        type="button"
                        className="lx-link"
                        onClick={handleSendOTP}
                        disabled={otpLoading}
                        style={{ ...textLink, fontSize: 12 }}
                      >
                        {t('auth.resendOtp')}
                      </button>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || formData.otp.length < 4}
                  className={`lx-btn${(loading || formData.otp.length < 4) ? ' sl-btn-disabled' : ''}`}
                  style={{ ...primaryBtn(loading || formData.otp.length < 4), marginTop: 8 }}
                >
                  {loading ? <><span style={spinner(false)} /><span>{t('auth.verifying')}</span></> : t('auth.verifySignIn')}
                </button>
              </>
            )}
          </form>
        )}
      </div>
    </>
  );

  const formPanel = (
    <main style={{
      flex: '1 1 50%', display: 'flex', alignItems: isDesktop ? 'center' : 'flex-start', justifyContent: 'center',
      padding: isDesktop ? '72px 48px' : '28px 16px 48px', boxSizing: 'border-box',
      minHeight: isDesktop ? '100vh' : 'auto',
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div className="lx-fade-up lx-d1" style={kicker({ color: L.goldDeep, fontSize: 12, letterSpacing: '.26em', marginBottom: 14 })}>
          {tr('员工登录 · Staff', 'Staff · 员工登录')}
        </div>
        <h1 className="lx-fade-up lx-d2" style={display(isDesktop ? 52 : 40, { marginBottom: 12 })}>
          {forgotMode ? t('auth.resetPassword') : tr('欢迎回来', 'Welcome back')}
        </h1>
        <p className="lx-fade-up lx-d2" style={{
          fontFamily: L.sans, fontWeight: 300, fontSize: 14.5, lineHeight: 1.6, color: L.ink2, margin: '0 0 34px',
        }}>
          {tr('红娘、陪跑老师与管理员登录后台', 'Sign in to the dashboard — for matchmakers, coaches and admins')}
        </p>

        {forgotMode ? forgotFlow : loginFlow}

        {!isDesktop && (
          <div className="lx-fade lx-d6" style={{
            marginTop: 44, textAlign: 'center', fontFamily: L.serif, fontStyle: 'italic', fontSize: 16, color: MUTE,
          }}>
            {tr('每一段良缘，都始于用心的成全。', 'Behind every match, a careful hand.')}
          </div>
        )}
      </div>
    </main>
  );

  return (
    <div className="lx-root sl-root" style={{
      position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: isDesktop ? 'row' : 'column',
    }}>
      <LuxeStyles />
      <style>{PAGE_CSS}</style>
      {langToggle}
      {isDesktop ? coverPanel : mobileMasthead}
      {formPanel}
    </div>
  );
};

export default Login;
